import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { InputGroup, ListGroup } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { useTranslate } from "../../../hooks/LanguageProvider";
import { searchItems } from "../../../config/search/search.data";
import { SearchItem } from "../../../config/search/search.types";
import { useAuth } from "../../../context/AuthContext";
import usePermissions from "../../../hooks/usePermissions";

const normalize = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

type SearchItemResolved = {
  id: number;
  title: string;
  content: string;
  route: string;
  permissionId?: number;
  allowedRoles?: number[];
};

const NavbarSearch: React.FC = () => {
  const { translate } = useTranslate();
  const { user } = useAuth();
  const { userPermissions } = usePermissions(
    user ? String(user.id_role) : null,
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchVisible, setSearchVisible] = useState(false);

  // 1) Items traduits (dépend de la langue)
  const resolvedItems: SearchItemResolved[] = useMemo(() => {
    return (searchItems as SearchItem[]).map((it) => ({
      id: it.id,
      route: it.route,
      permissionId: it.permissionId,
      allowedRoles: it.allowedRoles,
      title: translate(it.title),
      content: translate(it.content),
    }));
  }, [translate]);

  // 2) Filtrage (pas besoin de useState filteredPages)
  const filteredItems = useMemo(() => {
    const term = normalize(searchTerm.trim());
    if (!term) return [];
    return resolvedItems.filter((item) => {
      const hasRole =
        !item.allowedRoles ||
        (user !== null && item.allowedRoles.includes(user.id_role));
      const hasPermission =
        item.permissionId === undefined ||
        userPermissions.some(
          (permission) =>
            permission.id_permission === item.permissionId &&
            permission.can_read === 1,
        );

      return (
        hasRole &&
        hasPermission &&
        (normalize(item.title).includes(term) ||
          normalize(item.content).includes(term))
      );
    });
  }, [searchTerm, resolvedItems, user, userPermissions]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setSearchTerm(val);
    setSearchVisible(true);
  };

  const handleItemClick = () => {
    setSearchVisible(false);
    setSearchTerm("");
  };

  return (
    <div className="search-container hide-on-mobile" style={{ position: "relative" }}>
      <InputGroup>
        <InputGroup.Text id="search-icon">
          <i className="fas fa-search"></i>
        </InputGroup.Text>

        <Form.Control
          type="text"
          value={searchTerm}
          onChange={handleSearch}
          onFocus={() => setSearchVisible(true)}
          placeholder={translate("Search here") + "..."}
        />
      </InputGroup>

      {isSearchVisible && searchTerm && filteredItems.length > 0 && (
        <ListGroup
          className="results-container"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxHeight: "400px",
            border: "1px solid #ddd",
            borderRadius: "4px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            zIndex: 10,
            overflowY: "auto",
          }}
        >
          {filteredItems.slice(0, 20).map((item) => (
            <ListGroup.Item key={item.id} className="result-item" onClick={handleItemClick}>
              <Link
                to={item.route}
                className="d-block text-decoration-none text-reset"
              >
                <strong>{item.title}</strong>: {item.content}
              </Link>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

export default NavbarSearch;
